import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Camera, X, Check, Loader2 } from 'lucide-react';
import { storesApi } from '../../api/endpoints/stores';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Alert';
import { CURRENCIES, LANGUAGES, getLanguageLabel } from '../../lib/constants';
import { ApiError } from '../../types/api';
import { nameToSlug, sanitizeSlug } from '../../lib/slug';

const MAX_LOGO_SIZE = 5 * 1024 * 1024; // 5MB

const storeSchema = z.object({
  name: z.string().min(1, 'Required'),
  storeId: z
    .string()
    .min(3, 'Min 3 characters')
    .max(30, 'Max 30 characters')
    .regex(/^[a-z0-9-]+$/, 'Only lowercase letters, numbers, and hyphens'),
  description: z.string().optional(),
  currency: z.string().optional(),
  language: z.string().optional(),
});

type StoreFormValues = z.infer<typeof storeSchema>;
type CheckState = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Inline create-store modal mounted on the stores list (`/`). Matches the
 * legacy `views/myStore/stores.ejs` flow — popup over the list, not a
 * separate page. Includes Arabic→Latin transliteration of the store name
 * into a URL slug + a 2-second-debounced server check for availability +
 * inline 409 handling on submit.
 */
export function CreateStoreModal({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Once the user has edited the slug field by hand, stop overwriting it
  // when they keep typing the store name. Matches the legacy stores.ejs
  // flow ("slugTouched" gate).
  const [slugTouched, setSlugTouched] = useState(false);
  const [checkState, setCheckState] = useState<CheckState>('idle');
  // Server-confirmed conflict on submit. Set from the mutation's onError so
  // the inline field error survives even if the live check missed it (race).
  const [submitConflict, setSubmitConflict] = useState(false);

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } =
    useForm<StoreFormValues>({
      resolver: zodResolver(storeSchema),
      defaultValues: { currency: 'SAR', language: 'ar', storeId: '' },
    });

  const storeIdValue = watch('storeId');
  const nameValue = watch('name');

  // Wipe state every time the modal opens so a previous cancel/submit
  // doesn't leak into the next session.
  useEffect(() => {
    if (!isOpen) return;
    reset({ name: '', storeId: '', description: '', currency: 'SAR', language: 'ar' });
    setSlugTouched(false);
    setCheckState('idle');
    setSubmitConflict(false);
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
    setLogoError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-fill the slug from the name while the user hasn't touched it.
  // nameToSlug transliterates Arabic — هدا → "hda".
  useEffect(() => {
    if (slugTouched) return;
    const next = nameToSlug(nameValue || '');
    if (next !== storeIdValue) {
      setValue('storeId', next, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameValue, slugTouched]);

  // Debounced availability check (2 s after the last keystroke).
  useEffect(() => {
    setSubmitConflict(false);
    if (!storeIdValue) { setCheckState('idle'); return; }
    if (!/^[a-z0-9-]{3,30}$/.test(storeIdValue)) { setCheckState('invalid'); return; }

    setCheckState('checking');
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const res = await storesApi.checkId(storeIdValue);
        if (cancelled) return;
        if (res.reason === 'invalid') setCheckState('invalid');
        else setCheckState(res.available ? 'available' : 'taken');
      } catch {
        if (!cancelled) setCheckState('idle');
      }
    }, 2000);

    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [storeIdValue]);

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE) {
      setLogoError(t('stores.logoTooLarge', 'Logo must be under 5MB'));
      return;
    }
    setLogoError(null);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(null);
    setLogoError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleStoreIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugTouched(true);
    setValue('storeId', sanitizeSlug(e.target.value), { shouldValidate: true });
  };

  const mutation = useMutation({
    mutationFn: async (data: StoreFormValues) => {
      let logo: string | undefined;
      if (logoFile) {
        logo = await fileToBase64(logoFile);
      }
      return storesApi.create({ ...data, logo });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      toast('success', t('stores.createSuccess'));
      onClose();
      navigate(`/${result.id || result.storeId}`);
    },
    onError: (err) => {
      // 409 from POST /stores when the slug got claimed between the live
      // check and the submit — surface inline, not just a toast.
      if (err instanceof ApiError && (err.status === 409 || err.message === 'store_id_taken')) {
        setCheckState('taken');
        setSubmitConflict(true);
        return;
      }
      toast('error', t('common.error'));
    },
  });

  const footer = (
    <>
      <Button type="button" variant="secondary" onClick={onClose} disabled={mutation.isPending}>
        {t('common.cancel')}
      </Button>
      <Button
        type="submit"
        form="create-store-form"
        isLoading={mutation.isPending}
        disabled={checkState === 'taken' || checkState === 'invalid' || checkState === 'checking'}
      >
        {t('stores.createStore')}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('stores.createStore')} size="lg" footer={footer}>
      <p className="-mt-1 mb-4 text-sm text-text-muted">{t('stores.createStoreDesc')}</p>

      <form
        id="create-store-form"
        onSubmit={handleSubmit((data) => mutation.mutate(data))}
        className="space-y-5"
      >
        {/* Store Logo Upload */}
        <div className="flex flex-col items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleLogoSelect}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="relative flex h-20 w-20 items-center justify-center rounded-full border-2 border-dashed border-border-default bg-bg-field transition-colors hover:border-accent hover:bg-bg-hover overflow-hidden"
          >
            {logoPreview ? (
              <img
                src={logoPreview}
                alt={t('stores.logoPreview', 'Logo preview')}
                className="h-full w-full object-cover"
              />
            ) : (
              <Camera className="h-6 w-6 text-text-muted" />
            )}
          </button>
          <span className="text-xs text-text-muted">{t('stores.uploadLogo', 'Upload Logo')}</span>
          {logoFile && (
            <button
              type="button"
              onClick={handleLogoRemove}
              className="flex items-center gap-1 text-xs text-danger hover:underline"
            >
              <X className="h-3 w-3" />
              {t('common.remove', 'Remove')}
            </button>
          )}
          {logoError && <p className="text-xs text-danger">{logoError}</p>}
        </div>

        <Input
          id="name"
          label={t('stores.storeName')}
          required
          error={errors.name?.message}
          {...register('name')}
        />

        {/* Store ID / Slug — auto-filled from the name via Arabic→Latin
            transliteration; user can override; live-checked 2 s after
            the last keystroke. */}
        <div>
          <Input
            id="storeId"
            label={t('stores.storeId', 'Store ID')}
            placeholder="mystore"
            required
            dir="ltr"
            error={
              errors.storeId?.message ||
              (submitConflict ? t('stores.storeIdTakenError') : undefined)
            }
            {...register('storeId', { onChange: handleStoreIdChange })}
          />
          {storeIdValue && !errors.storeId && (
            <p className="text-xs text-text-muted mt-1" dir="ltr">
              https://{storeIdValue}.00stores.com
            </p>
          )}
          {storeIdValue && checkState === 'checking' && (
            <p className="mt-1 flex items-center gap-1 text-xs text-text-muted">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('stores.storeIdChecking')}
            </p>
          )}
          {storeIdValue && checkState === 'available' && (
            <p className="mt-1 flex items-center gap-1 text-xs text-success">
              <Check className="h-3 w-3" />
              {t('stores.storeIdAvailable')}
            </p>
          )}
          {storeIdValue && checkState === 'taken' && (
            <p className="mt-1 text-xs text-danger">{t('stores.storeIdTaken')}</p>
          )}
          {storeIdValue && checkState === 'invalid' && !errors.storeId && (
            <p className="mt-1 text-xs text-danger">{t('stores.storeIdInvalid')}</p>
          )}
        </div>

        <Textarea
          id="description"
          label={t('common.description')}
          rows={3}
          {...register('description')}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            id="currency"
            label={t('settings.currency')}
            options={CURRENCIES.map((c) => ({ value: c, label: c }))}
            {...register('currency')}
          />
          <Select
            id="language"
            label={t('settings.defaultLanguage')}
            options={LANGUAGES.map((l) => ({ value: l, label: getLanguageLabel(l) }))}
            {...register('language')}
          />
        </div>
      </form>
    </Modal>
  );
}
